import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { getApplicantSystemValue } from "@/pages/recruit/candidates/applicant-system-fields.ts";
import ContactButton from "@/pages/recruit/candidates/components/ContactButton.tsx";
import { ContactTypes } from "@/types/common.types.ts";
import { ApiCustomField, ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import {
  ApiApplicantContact,
  ApiApplicantShort,
  ContactType,
} from "@/types/recruit/applicant.types.ts";
import { ButtonIcon, FontAwesomeIcon, ListItem, Stack, useToasts } from "@skailer-com/uikit";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";

const getTelegramUrl = (username: string) => {
  const cleanUsername = username
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/^@/, "");
  const telegramPath = cleanUsername.replace(/^t\.me(?=\/|$)/, "telegram.me");

  return /^telegram\.me(?:\/|$)/.test(telegramPath)
    ? `https://${telegramPath}`
    : `https://telegram.me/${telegramPath}`;
};

const formatPhone = (phone: string) => {
  const cleaned = phone.replace(/\D/g, "");
  if (!cleaned) {
    return undefined;
  }

  if (/^8\d{10}$/.test(cleaned)) {
    return cleaned.replace(/^8/, "7");
  }

  return cleaned;
};

export default function useApplicantCustomFieldsHook() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { calculateAge, renderPartialDate } = useFormatter();
  const { renderCustomFieldListItem, getFieldTitle } = useCustomFieldsHook();

  const renderPhoneListItem = (preTitle: string, phone: string) => {
    const formattedPhone = formatPhone(phone);

    return (
      <ListItem
        preTitle={preTitle}
        title={phone}
        valueSlot={
          formattedPhone && (
            <Stack direction={"horizontal"} gap={"xs"}>
              <ContactButton type={ContactTypes.telegram} contact={formattedPhone} />
              <ContactButton type={ContactTypes.whatsapp} contact={formattedPhone} />
              <ContactButton type={ContactTypes.viber} contact={formattedPhone} />
            </Stack>
          )
        }
      />
    );
  };

  const renderEmailListItem = (preTitle: string, email: string) => (
    <ListItem
      preTitle={preTitle}
      title={email}
      onClick={() => {
        copyTextWithNotify(email).then((copied) => {
          if (!copied) return;
          showInfo({ title: t("common:email_copied") });
        });
      }}
      valueSlot={
        <ButtonIcon icon={<FontAwesomeIcon icon={"fa-light fa-copy"} />} variant={"plain"} />
      }
    />
  );

  const renderLinkListItem = (preTitle: string, title: string, url: string, truncate = false) => (
    <ListItem
      preTitle={preTitle}
      title={title}
      truncate={truncate}
      to={url}
      blank={true}
      valueSlot={
        <ButtonIcon
          icon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            copyTextWithNotify(title).then((copied) => {
              if (!copied) return;
              showInfo({ title: t("common:link_copied") });
            });
          }}
          variant="plain"
        />
      }
    />
  );

  const renderApplicantFieldListItem = (
    field: ApiField,
    applicant: ApiApplicantShort,
    customFields: ApiCustomField[] = []
  ): ReactNode | undefined => {
    if (field.type === ApiFieldTypes.custom) {
      return renderCustomFieldListItem(field, customFields);
    }

    const label = getFieldTitle(field);

    switch (field.type) {
      case ApiFieldTypes.applicant_date_birth: {
        const birthDate = applicant.birth_date ? renderPartialDate(applicant.birth_date) : null;
        if (!applicant.birth_date || !birthDate) return undefined;
        const age = calculateAge(applicant.birth_date);

        return (
          <ListItem
            preTitle={label}
            title={birthDate}
            value={age > 0 ? t("plurals:plural_year", { count: age }) : undefined}
          />
        );
      }
      case ApiFieldTypes.applicant_phone:
        return applicant.contacts.phone
          ? renderPhoneListItem(label, applicant.contacts.phone)
          : undefined;
      case ApiFieldTypes.applicant_email:
        return applicant.contacts.email
          ? renderEmailListItem(label, applicant.contacts.email)
          : undefined;
      case ApiFieldTypes.applicant_telegram: {
        const telegram = applicant.contacts.telegram_username;
        return telegram ? renderLinkListItem(label, telegram, getTelegramUrl(telegram)) : undefined;
      }
      case ApiFieldTypes.applicant_additional_contacts:
        return applicant.additional_contacts.length ? (
          <>
            {applicant.additional_contacts.map((contact, index) => (
              <Fragment key={index}>{renderApplicantContactListItem(contact)}</Fragment>
            ))}
          </>
        ) : undefined;
      default: {
        const value = getApplicantSystemValue(field.type, applicant);
        return typeof value === "string" && value ? (
          <ListItem preTitle={label} title={value} />
        ) : undefined;
      }
    }
  };

  const renderApplicantContactListItem = ({ type, value, label }: ApiApplicantContact) => {
    const withLabel = (title: string) => (label ? `${title} • ${label}` : title);

    switch (type) {
      case ContactType.phone:
        return renderPhoneListItem(withLabel(t("core:phone")), value);
      case ContactType.email:
        return renderEmailListItem(withLabel(t("core:email")), value);
      case ContactType.link:
        return renderLinkListItem(withLabel(t("common:link")), value, value, true);
      case ContactType.telegram:
        return renderLinkListItem(withLabel(t("common:telegram")), value, getTelegramUrl(value));
      default:
        return <ListItem preTitle={withLabel(t("common:other"))} title={value} />;
    }
  };

  return { renderApplicantFieldListItem };
}
